import { useRef, useCallback, useState, useEffect } from 'react';

export type CarouselGapToken = 'xs' | 'sm' | 'md' | 'lg';
export type CarouselStep = 'page' | number;

const GAP_PX: Record<CarouselGapToken, number> = { xs: 4, sm: 8, md: 16, lg: 24 };

export type CarouselNavigationOptions = {
  gap?: CarouselGapToken;
  step?: CarouselStep;
};

export type CarouselNavigationReturn = {
  trackRef: React.RefObject<HTMLUListElement>;
  canScrollLeft: boolean;
  canScrollRight: boolean;
  currentPage: number;
  totalPages: number;
  handlePrevious: () => void;
  handleNext: () => void;
  scrollToPage: (page: number) => void;
  scrollToStart: () => void;
  scrollToEnd: () => void;
  handleScroll: () => void;
};

function getStepDistance(track: HTMLUListElement, step: CarouselStep, gapPx: number): number {
  if (step === 'page') return track.clientWidth;

  const items = Array.from(track.children) as Array<HTMLElement>;
  if (items.length === 0) return track.clientWidth;

  let distance = 0;
  for (let i = 0; i < step && i < items.length; i++) {
    distance += items[i].offsetWidth;
  }
  distance += gapPx * Math.min(step, items.length - 1);
  return distance;
}

/** @internal Exported for testing */
export function computePages(track: HTMLUListElement, step: CarouselStep, gapPx: number): number {
  const maxScroll = track.scrollWidth - track.clientWidth;
  if (maxScroll <= 0) return 1;

  const stepDist = step === 'page' ? track.clientWidth : getStepDistance(track, step, gapPx);
  if (stepDist <= 0) return 1;
  return Math.max(1, Math.round(maxScroll / stepDist) + 1);
}

export function useCarouselNavigation({
  gap = 'md',
  step = 'page',
}: CarouselNavigationOptions = {}): CarouselNavigationReturn {
  const trackRef = useRef<HTMLUListElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const rafRef = useRef<number | null>(null);

  const gapPx = GAP_PX[gap];

  const updateScrollState = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;

    const { scrollLeft, scrollWidth, clientWidth } = track;
    const maxScroll = scrollWidth - clientWidth;

    const pages = computePages(track, step, gapPx);
    setTotalPages(pages);

    let page = 0;
    if (maxScroll > 0) {
      page = Math.round((scrollLeft / maxScroll) * (pages - 1));
      page = Math.min(page, pages - 1);
    }
    setCurrentPage(page);
    setCanScrollLeft(page > 0);
    setCanScrollRight(page < pages - 1);
  }, [step, gapPx]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    updateScrollState();

    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(updateScrollState);
      observer.observe(track);
    }

    return () => {
      observer?.disconnect();
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [updateScrollState]);

  const scrollToPosition = useCallback((left: number) => {
    trackRef.current?.scrollTo({ left, behavior: 'smooth' });
  }, []);

  const handlePrevious = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;

    if (step === 'page') {
      const prevPage = Math.max(0, currentPage - 1);
      const maxScroll = track.scrollWidth - track.clientWidth;
      const pages = computePages(track, step, gapPx);
      const target = pages <= 1 ? 0 : (prevPage / (pages - 1)) * maxScroll;
      scrollToPosition(target);
    } else {
      const dist = getStepDistance(track, step, gapPx);
      scrollToPosition(track.scrollLeft - dist);
    }
  }, [scrollToPosition, step, gapPx, currentPage]);

  const handleNext = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;

    if (step === 'page') {
      const pages = computePages(track, step, gapPx);
      const nextPage = Math.min(pages - 1, currentPage + 1);
      const maxScroll = track.scrollWidth - track.clientWidth;
      const target = pages <= 1 ? 0 : (nextPage / (pages - 1)) * maxScroll;
      scrollToPosition(target);
    } else {
      const dist = getStepDistance(track, step, gapPx);
      scrollToPosition(track.scrollLeft + dist);
    }
  }, [scrollToPosition, step, gapPx, currentPage]);

  const scrollToPage = useCallback(
    (page: number) => {
      const track = trackRef.current;
      if (!track) return;

      const maxScroll = track.scrollWidth - track.clientWidth;
      if (totalPages <= 1) {
        scrollToPosition(0);
        return;
      }
      const targetScroll = (page / (totalPages - 1)) * maxScroll;
      scrollToPosition(targetScroll);
    },
    [totalPages, scrollToPosition],
  );

  const scrollToStart = useCallback(() => {
    scrollToPosition(0);
  }, [scrollToPosition]);

  const scrollToEnd = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    scrollToPosition(track.scrollWidth);
  }, [scrollToPosition]);

  const handleScroll = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
    }
    rafRef.current = requestAnimationFrame(() => {
      updateScrollState();
      rafRef.current = null;
    });
  }, [updateScrollState]);

  return {
    trackRef,
    canScrollLeft,
    canScrollRight,
    currentPage,
    totalPages,
    handlePrevious,
    handleNext,
    scrollToPage,
    scrollToStart,
    scrollToEnd,
    handleScroll,
  };
}
